import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';

import { FileType } from '../models/file-item.model';
import { PaintMarkerName } from '../models/paint-marker-name.enum';
import { BrowserPaintService } from './browser-paint.service';
import { OfficeRouteItem, OfficeRouteService } from './office-route.service';
import { PageVisibilityService } from './page-visibility.service';
import { PerformanceService } from './performance.service';
import { VisuallyReadyService } from './visually-ready-service';

@Injectable({
    providedIn: 'root'
})
export class BrowserPaintMeasurementService {
    private readonly queuedEvents: Set<PaintMarkerName> = new Set();
    private readonly markedEvents: Set<string> = new Set();
    private paintListenerHandle: number;
    private currentRoute: OfficeRouteItem;

    constructor(
        private readonly performanceService: PerformanceService,
        @Inject(PLATFORM_ID) private readonly platformId: string,
        private readonly pageVisibilityService: PageVisibilityService,
        private readonly browserPaintService: BrowserPaintService,
        routeService: OfficeRouteService,
        @Optional() private readonly visuallyReadyService?: VisuallyReadyService
    ) {
        this.setupOnHiddenListener();
        routeService.subscribeToRouteChangeStart(events => this.currentRoute = events.currentRoute);
    }

    public recordWhenPainted(markerName: PaintMarkerName) {
        const routeIdentifier = this.currentRoute && this.currentRoute.route ? `_${this.currentRoute.route}${this.currentRoute.app ? FileType[this.currentRoute.app] : ''}` : '';
        const markerNameWithRouteIdentifier = markerName + routeIdentifier;

        // Only browser's have the `requestAnimationFrame` API.
        if (!isPlatformBrowser(this.platformId) || this.markedEvents.has(markerNameWithRouteIdentifier) || !this.pageVisibilityService.isVisible()) {
            return;
        }

        this.queuedEvents.add(markerName);
        this.markedEvents.add(markerNameWithRouteIdentifier);

        if (this.paintListenerHandle) {
            return;
        }

        // Approximation of the browser's 'paint' of the frame.
        // See: https://microsoft.sharepoint.com/teams/edgeshare/SitePages/Perf-Club-Training-%E2%80%93-Advanced.aspx
        // => Accurately Measuring Layout
        this.paintListenerHandle = this.browserPaintService.runAfterPaint(() => {
            if (this.pageVisibilityService.isVisible()) {
                this.queuedEvents.forEach(marker => {
                    this.performanceService.mark(`Paint_${marker}`);

                    if (this.visuallyReadyService) {
                        this.visuallyReadyService.mark(marker);
                    }
                });
            }

            this.resetListener();
        }, false);
    }

    private setupOnHiddenListener() {
        this.pageVisibilityService.addOnVisibilityChanged(state => {
            if (state === 'hidden') {
                this.cancelPaintListener();
            }
        });
    }

    private cancelPaintListener() {
        if (this.paintListenerHandle) {
            this.browserPaintService.cancel(this.paintListenerHandle);
        }

        this.resetListener();
    }

    private resetListener() {
        this.queuedEvents.clear();
        this.paintListenerHandle = null;
    }
}
